/** Instance-owned scene extracted from the separately maintained Steam Punk game.
 * The host supplies clock, randomness, DOM and lifecycle; no globals or score API are shared. */
export function installSteamScene(runtime) {
  runtime.draw = function () {
    runtime.ctx.save();
    if (runtime.game.shake > 0) {
      const t = runtime.now() * 0.07;
      const amount = runtime.game.shake * 5.5;
      runtime.ctx.translate(Math.sin(t) * amount, Math.cos(t * 1.31) * amount);
    }
    runtime.ctx.clearRect(0, 0, runtime.W, runtime.H);
    runtime.drawBoardScene();
    runtime.drawLauncher();
    runtime.drawLaunchHud();
    runtime.game.balls.forEach(runtime.drawBall);
    runtime.game.bursts.forEach(runtime.drawBurst);
    runtime.game.particles.forEach(runtime.drawParticle);
    runtime.game.floaters.forEach(runtime.drawFloater);
    runtime.drawChaosShimmer();
    runtime.drawScreenFlash();
    runtime.drawPressureWarning();
    if (
      (!runtime.game.volleyActive ||
        runtime.isRapidLaunchMode() ||
        runtime.canRelaunchDuringVolley()) &&
      runtime.game.status !== 'gameover'
    )
      runtime.drawAim();
    runtime.ctx.restore();
  };
  runtime.boardShiftCueOffset = function () {
    if (!runtime.game.boardShiftCue || !runtime.game.boardShiftCueMax) return 0;
    const progress = runtime.clamp(
      runtime.game.boardShiftCue / runtime.game.boardShiftCueMax,
      0,
      1,
    );
    return -runtime.ROW_STEP * progress * progress;
  };
  runtime.shouldUseBoardSceneCache = function () {
    return (
      runtime.MOBILE_QUALITY &&
      runtime.isPressureMode() &&
      runtime.game.shake <= 0 &&
      Boolean(runtime.boardSceneCtx)
    );
  };
  runtime.drawBoardSceneDirect = function () {
    runtime.drawBackdrop();
    runtime.drawDanger();
    runtime.game.planets.forEach(runtime.drawPlanet);
    runtime.game.walls.forEach(runtime.drawWall);
    runtime.game.orbs.forEach(runtime.drawOrb);
    runtime.game.bricks.forEach((brick) => {
      if (!brick.dead) runtime.drawBrick(brick);
    });
  };
  runtime.drawBoardScene = function () {
    if (!runtime.shouldUseBoardSceneCache()) {
      runtime.drawBoardSceneDirect();
      return;
    }
    if (runtime.boardSceneDirty || !runtime.boardSceneReady) {
      runtime.drawBoardSceneDirect();
      runtime.boardSceneCtx.clearRect(0, 0, runtime.W, runtime.H);
      runtime.boardSceneCtx.drawImage(runtime.canvas, 0, 0);
      runtime.boardSceneReady = true;
      runtime.boardSceneDirty = false;
      runtime.ctx.clearRect(0, 0, runtime.W, runtime.H);
    }
    const offsetY = runtime.boardShiftCueOffset();
    runtime.ctx.drawImage(runtime.boardSceneCanvas, 0, offsetY);
    if (offsetY) {
      runtime.ctx.save();
      runtime.ctx.globalAlpha = runtime.clamp(
        runtime.game.boardShiftCue / Math.max(0.001, runtime.game.boardShiftCueMax),
        0,
        0.32,
      );
      runtime.ctx.fillStyle = 'rgba(255, 209, 102, 0.18)';
      runtime.ctx.fillRect(0, 52, runtime.W, runtime.ROW_STEP + 18);
      runtime.ctx.restore();
    }
  };
  runtime.getTimedDangerRowsRemaining = function () {
    if (runtime.game.mode !== 'timed') return null;
    let closestBottom = -Infinity;
    for (const brick of runtime.game.bricks) {
      if (brick.dead || brick.y >= runtime.H) continue;
      closestBottom = Math.max(closestBottom, brick.y + brick.h);
    }
    if (!Number.isFinite(closestBottom)) return null;
    const rowsRemaining = Math.ceil((runtime.dangerY - closestBottom) / runtime.ROW_STEP);
    if (rowsRemaining > runtime.TIMED_DANGER_WARNING_ROWS) return null;
    return runtime.clamp(rowsRemaining, 0, runtime.TIMED_DANGER_WARNING_ROWS);
  };
  runtime.getPressureWarningState = function (now = runtime.now()) {
    if (runtime.game.status === 'gameover' || !runtime.game.modeRunStarted) return null;
    if (
      runtime.game.mode === 'speed' &&
      runtime.game.modeTimerRemaining <= runtime.SPEED_ENDGAME_WARNING_SECONDS
    ) {
      const remaining = Math.max(0, runtime.game.modeTimerRemaining);
      const progress = 1 - remaining / runtime.SPEED_ENDGAME_WARNING_SECONDS;
      return {
        label: 'TIME',
        value: String(Math.ceil(remaining)),
        progress,
        beat: 1.25 + progress * 3.75,
        now,
      };
    }
    const rowsRemaining = runtime.getTimedDangerRowsRemaining();
    if (rowsRemaining !== null) {
      const progress = 1 - rowsRemaining / runtime.TIMED_DANGER_WARNING_ROWS;
      return {
        label: 'ROWS',
        value: String(rowsRemaining),
        progress,
        beat: 1.15 + progress * 3.25,
        now,
      };
    }
    return null;
  };
  runtime.drawPressureWarning = function () {
    const warning = runtime.getPressureWarningState();
    if (!warning) return;
    const seconds = warning.now / 1000;
    const pulse = Math.max(0, Math.sin(seconds * Math.PI * 2 * warning.beat));
    const intensity = runtime.clamp(0.35 + warning.progress * 0.45 + pulse * 0.45, 0, 1);
    const textAlpha = runtime.clamp(0.22 + warning.progress * 0.35 + pulse * 0.35, 0, 0.92);
    const evaporate = 1 + warning.progress * 0.18 + pulse * 0.12;
    runtime.ctx.save();
    runtime.ctx.globalCompositeOperation = 'lighter';
    runtime.ctx.shadowColor = 'rgba(170,0,0,0.92)';
    runtime.ctx.shadowBlur = 24 + intensity * 42;
    runtime.ctx.strokeStyle = `rgba(150, 0, 0, ${0.38 + intensity * 0.42})`;
    runtime.ctx.lineWidth = 8 + intensity * 8;
    runtime.ctx.strokeRect(10, 10, runtime.W - 20, runtime.H - 20);
    runtime.ctx.strokeStyle = `rgba(255, 30, 30, ${0.22 + intensity * 0.35})`;
    runtime.ctx.lineWidth = 3 + intensity * 4;
    runtime.ctx.strokeRect(24, 24, runtime.W - 48, runtime.H - 48);
    runtime.ctx.textAlign = 'center';
    runtime.ctx.textBaseline = 'middle';
    runtime.ctx.font = `950 ${Math.round(160 * evaporate)}px 'Courier New', monospace`;
    runtime.ctx.fillStyle = `rgba(255, 20, 20, ${textAlpha})`;
    runtime.ctx.shadowColor = 'rgba(255,0,0,0.9)';
    runtime.ctx.shadowBlur = 38 + intensity * 48;
    runtime.ctx.fillText(warning.value, runtime.W / 2, runtime.H * 0.42 - warning.progress * 18);
    runtime.ctx.font = '950 24px Orbitron, Arial, sans-serif';
    runtime.ctx.fillStyle = `rgba(255, 210, 190, ${0.45 + intensity * 0.4})`;
    runtime.ctx.fillText(warning.label, runtime.W / 2, runtime.H * 0.42 + 96);
    runtime.ctx.restore();
  };
  runtime.renderBackdropCache = function () {
    if (!runtime.backdropCtx) return;
    const target = runtime.backdropCtx;
    if (runtime.nebulaImage.complete && runtime.nebulaImage.naturalWidth) {
      const scale = Math.max(
        runtime.W / runtime.nebulaImage.naturalWidth,
        runtime.H / runtime.nebulaImage.naturalHeight,
      );
      const sw = runtime.W / scale;
      const sh = runtime.H / scale;
      const sx = (runtime.nebulaImage.naturalWidth - sw) / 2;
      const sy = (runtime.nebulaImage.naturalHeight - sh) / 2;
      target.save();
      target.filter = 'contrast(1.18) brightness(1.08) saturate(1.26)';
      target.drawImage(runtime.nebulaImage, sx, sy, sw, sh, 0, 0, runtime.W, runtime.H);
      target.restore();
      const veil = target.createLinearGradient(0, 0, 0, runtime.H);
      veil.addColorStop(0, 'rgba(5,7,18,0.18)');
      veil.addColorStop(0.45, 'rgba(5,7,18,0.08)');
      veil.addColorStop(1, 'rgba(5,7,18,0.28)');
      target.fillStyle = veil;
      target.fillRect(0, 0, runtime.W, runtime.H);
    } else {
      const gradient = target.createLinearGradient(0, 0, runtime.W, runtime.H);
      gradient.addColorStop(0, '#071024');
      gradient.addColorStop(0.48, '#11183a');
      gradient.addColorStop(1, '#031b1f');
      target.fillStyle = gradient;
      target.fillRect(0, 0, runtime.W, runtime.H);
    }
    target.strokeStyle = 'rgba(255,255,255,0.07)';
    target.lineWidth = 1;
    for (let x = 0; x <= runtime.W; x += 72) {
      target.beginPath();
      target.moveTo(x, 0);
      target.lineTo(x, runtime.H);
      target.stroke();
    }
    for (let y = 0; y <= runtime.H; y += 72) {
      target.beginPath();
      target.moveTo(0, y);
      target.lineTo(runtime.W, y);
      target.stroke();
    }
    runtime.backdropReady = true;
  };
  runtime.drawBackdrop = function () {
    if (!runtime.backdropReady) runtime.renderBackdropCache();
    if (runtime.backdropReady) runtime.ctx.drawImage(runtime.backdropCanvas, 0, 0);
  };
  runtime.drawDanger = function () {
    runtime.ctx.fillStyle = 'rgba(255,138,61,0.1)';
    runtime.ctx.fillRect(0, runtime.dangerY - 1, runtime.W, 6);
    runtime.ctx.strokeStyle = 'rgba(255,209,102,0.82)';
    runtime.ctx.lineWidth = 2.5;
    runtime.ctx.setLineDash([18, 9, 4, 9]);
    runtime.ctx.beginPath();
    runtime.ctx.moveTo(18, runtime.dangerY);
    runtime.ctx.lineTo(runtime.W - 18, runtime.dangerY);
    runtime.ctx.stroke();
    runtime.ctx.strokeStyle = 'rgba(102,45,8,0.85)';
    runtime.ctx.lineWidth = 1;
    runtime.ctx.beginPath();
    runtime.ctx.moveTo(18, runtime.dangerY + 5);
    runtime.ctx.lineTo(runtime.W - 18, runtime.dangerY + 5);
    runtime.ctx.stroke();
    runtime.ctx.setLineDash([]);
  };
  runtime.drawBrick = function (brick) {
    const color = runtime.brickColor(brick);
    brick.heat = Math.max(0, brick.heat - 0.055);
    const sprite = runtime.getBrickSprite(color);
    if (brick.heat > 0.08) {
      runtime.ctx.save();
      runtime.ctx.shadowColor = color;
      runtime.ctx.shadowBlur = 8 + brick.heat * 16;
      runtime.ctx.drawImage(sprite, Math.round(brick.x), Math.round(brick.y));
      runtime.ctx.restore();
    } else {
      runtime.ctx.drawImage(sprite, Math.round(brick.x), Math.round(brick.y));
    }
    runtime.ctx.fillStyle = '#fff8dc';
    runtime.ctx.font = '900 12px Sora, Inter, sans-serif';
    runtime.ctx.textAlign = 'center';
    runtime.ctx.textBaseline = 'middle';
    runtime.ctx.fillText(brick.hp, brick.x + brick.w / 2, brick.y + brick.h / 2 + 1);
  };
  runtime.getBrickSprite = function (color) {
    const key = `${color}:${runtime.BRICK_H}`;
    if (runtime.brickSpriteCache.has(key)) return runtime.brickSpriteCache.get(key);
    const sprite = document.createElement('canvas');
    sprite.width = runtime.BRICK_H;
    sprite.height = runtime.BRICK_H;
    const target = sprite.getContext('2d');
    const gleam = target.createLinearGradient(0, 0, runtime.BRICK_H, runtime.BRICK_H);
    gleam.addColorStop(0, '#fff1b4');
    gleam.addColorStop(0.16, runtime.brightenColor(color));
    gleam.addColorStop(0.52, color);
    gleam.addColorStop(1, '#391405');
    runtime.roundRectCtx(target, 0.5, 0.5, runtime.BRICK_H - 1, runtime.BRICK_H - 1, 5);
    target.fillStyle = gleam;
    target.fill();
    target.strokeStyle = 'rgba(255,238,180,0.58)';
    target.lineWidth = 1.2;
    target.stroke();
    target.fillStyle = 'rgba(37,12,3,0.34)';
    runtime.roundRectCtx(target, 3, 3, runtime.BRICK_H - 6, runtime.BRICK_H - 6, 3);
    target.fill();
    target.fillStyle = 'rgba(255,244,204,0.3)';
    runtime.roundRectCtx(target, 4, 3, Math.max(4, runtime.BRICK_H - 8), 3, 2);
    target.fill();
    runtime.brickSpriteCache.set(key, sprite);
    return sprite;
  };
}
